import React, { memo, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { isEmptyArray, isNil } from '@common/utils';
import SatButton from '@components/common/satUI/SatButton';
import SatRadio from '@components/common/satUI/SatRadio';
import ComingSoonPopup from '@components/common/ComingSoonPopup';
import SatModal from '@components/common/satUI/SatModal';

/**
 * 부가서류 재발행 체크박스 목록
 */
const ReissueDocCheckboxList = memo(
  ({ optionList, selectedList, setSelectedList, checkAllable }) => {
    const [showCommingSoon, setShowCommingSoon] = useState(false);

    /**
     * 전체 선택 여부 조회
     */
    const isAll = useCallback(
      (optionList, selectedList) =>
        optionList
          .filter(a => a.isUse) // to do 부가서류 재발행 모든 항목 개발 완료시 filter 삭제
          .map(a => a.value)
          .every(a => selectedList.includes(a)),
      [],
    );

    /**
     * check box container click handler\
     * 준비중 입니다 표기용
     */
    const handleContainerClick = useCallback(
      option => () => {
        !option.isUse && setShowCommingSoon(true);
      },
      [],
    );

    /**
     * 재발행 하겠습니다 / 재발행하지 않겠습니다 change handler
     */
    const handleReissueCheckboxChange = useCallback(
      (optionList, selectedList) => () =>
        isAll(optionList, selectedList)
          ? setSelectedList([])
          : setSelectedList(optionList.filter(a => a.isUse).map(a => a.value)), // to do 부가서류 재발행 모든 항목 개발 완료시 filter 삭제
      [],
    );

    /**
     * 부가서류 재발행 대상 체크박스 change handler
     */
    const handleAdditionalDocReissueCheckboxChange = useCallback(
      option => () => {
        setSelectedList(prev =>
          (prev ?? []).includes(option.value)
            ? prev.filter(a => option.value !== a)
            : prev.concat(option.value),
        );
      },
      [],
    );

    return (
      <div className="infor-list pdb-20">
        <div className="tit-area">
          <h2 className="tit-con">부가서류 재발행</h2>
          <div className="check-wrap chk-style">
            <input
              type="checkbox"
              name="reissue-check-all"
              id="reissue-check-all-id"
              disabled={!checkAllable}
              checked={isAll(optionList, selectedList)}
              onChange={handleReissueCheckboxChange(optionList, selectedList)}
            />
            <label htmlFor="reissue-check-all-id">전체선택</label>
          </div>
        </div>
        <ul className="check-list columns-list">
          {optionList.map((option, idx) => (
            <li key={`checkbox-li-${idx}`}>
              <div
                className="check-wrap chk-style"
                onClick={handleContainerClick(option)}
              >
                <input
                  disabled={!!option.disabled}
                  type="checkbox"
                  name={`reissue-checkbox-${idx}`}
                  id={`reissue-checkbox-id-${idx}`}
                  checked={selectedList.includes(option.value)}
                  onChange={handleAdditionalDocReissueCheckboxChange(option)}
                />
                <label htmlFor={`reissue-checkbox-id-${idx}`}>
                  {option.label}
                </label>
              </div>
            </li>
          ))}
        </ul>
        <ComingSoonPopup
          visible={showCommingSoon}
          setVisible={() => setShowCommingSoon(false)}
        />
      </div>
    );
  },
);

ReissueDocCheckboxList.propTypes = {
  optionList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      idx: PropTypes.number,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  selectedList: PropTypes.arrayOf(PropTypes.string),
  setSelectedList: PropTypes.func.isRequired,
  checkAllable: PropTypes.bool,
};

ReissueDocCheckboxList.defaultProps = {
  selectedList: [],
  checkAllable: false,
};

/**
 * 청약보완서류 발행 팝업
 */
const AdditionalDocPopup = ({
  hide,
  postEachReissueReport,
  handleReissueRequest,
}) => {
  const [reissue, setReissue] = useState();
  const [selectedReissueDocList, setSelectedReissueDocList] = useState([]);
  const [isOpenMovePlanListPagePopup, setIsOpenMovePlanListPagePopup] =
    useState(false); // 설계목록 이동 알림 팝업

  const history = useHistory();

  // 청약서 재발행 옵션목록
  const [reissueEaOptionList] = useState([
    { label: '재발행 하겠습니다.', value: true },
    { label: '재발행하지 않겠습니다', value: false },
  ]);
  // 부가서류 재발행 옵션목록
  const [reissueDocOptionList, setReissueDocOptionList] = useState([
    {
      label: '알릴의무사항 (신규작성)',
      idx: 0,
      value: 'app_form_06',
      disabled: isNil(reissue) || reissue, // 재발행하지 않겠습니다 일때 활성화
      isUse: true,
    },
    {
      label: '특정 신체부위 질병 보장제한부 인수 특약 신청서',
      idx: 1,
      value: 'app_form_etc_14',
      disabled: isNil(reissue) || reissue, // 재발행하지 않겠습니다 일때 활성화
      isUse: true, // 부가 서류 재발행 개발 된 건지 구분하기 위한 필드, 모든 항목 개발 되면 삭제 될 필드, ture : 개발 완료, false : 미 개발 됨
    },
    {
      label: '보험금 감액 신청서',
      idx: 2,
      value: 'app_form_etc_15',
      disabled: isNil(reissue) || reissue,
      isUse: true,
    },
    // {
    //   label: '개인용 계약확인서',
    //   idx: 3,
    //   value: 'app_form_etc_29',
    //   disabled: false,
    // },
    // {
    //   label: 'VIP 고객 질문지',
    //   idx: 4,
    //   value: 'app_form_etc_06',
    //   disabled: false,
    // },
    {
      label: '금융소비자에 대한 강화된 상품 설명 확인서',
      idx: 5,
      value: 'app_form_etc_24',
      disabled: isNil(reissue) || reissue, // 재발행하지 않겠습니다 일때 활성화
      isUse: true,
    },
    // {
    //   label: '대출 납입일시중지 후 신계약 신청서',
    //   idx: 6,
    //   value: 'app_form_etc_04',
    //   disabled: false,
    // },
  ]);

  /**
   * 재발행하겠습니다 / 재발행하지 않겠습니다 change handler
   */
  const handleEaReissueChange = useCallback(option => {
    setReissue(option.value);
    if (option.value) {
      // 재발행 하겠습니다.
      handleReissueRequest();
      setSelectedReissueDocList([]);
    }
    // 재발행하지 않겠습니다인 경우, 특정 신체부위 질병 보장제한부 인수 특약 신청서만 선택 가능
    setReissueDocOptionList(prev =>
      prev.map(a =>
        Object.assign({}, a, {
          disabled: option.value || !a.isUse, // to do 부가서류 재발행 모든 항목 개발 완료시 disabled: option.value 로 수정
        }),
      ),
    );
  }, []);

  /**
   * 발행 버튼 click handler
   */
  const handleReissueButtonClick = useCallback(
    selectedReissueDocList => () => {
      const postEachReissueReportCallback = () => {
        history.push('/planList');
      };
      postEachReissueReport(
        selectedReissueDocList,
        postEachReissueReportCallback,
      );
    },
    [],
  );

  useEffect(() => {
    const cleanUp = () => {
      setReissue();
      setSelectedReissueDocList([]);
    };

    return cleanUp;
  }, []);

  return (
    <>
      <header className="con-header">
        <div className="page-navi">
          <SatButton
            styleClass="btn-back"
            label="이전"
            onClick={() => hide()}
          />
        </div>
        <h1 className="page-tit">청약보완서류 발행</h1>
      </header>

      <div className="con-wrap">
        <div className="wide-con-wrap">
          {/* className : pop-box -> wide-con-wrap */}
          {/* 청약서 재발행 */}
          <div className="infor-list pdb-20">
            <h2 className="tit-con">청약서 재발행</h2>
            <div className="check-box">
              <SatRadio
                value={reissue}
                options={reissueEaOptionList}
                onChange={handleEaReissueChange}
              />
            </div>
          </div>
          {/* 부가서류 재발행 */}
          <ReissueDocCheckboxList
            optionList={reissueDocOptionList}
            selectedList={selectedReissueDocList}
            setSelectedList={setSelectedReissueDocList}
            checkAllable={reissue === false}
          />
        </div>
      </div>

      <div className="con-bottom">
        <div className="btn-area">
          <div className="box-btns-center">
            <SatButton
              styleClass="btn-s blue btn-wide"
              label="발행"
              onClick={() => setIsOpenMovePlanListPagePopup(true)}
              disabled={isEmptyArray(selectedReissueDocList)}
            />
          </div>
        </div>
      </div>
      <SatModal
        visible={isOpenMovePlanListPagePopup}
        setVisible={() => {
          setIsOpenMovePlanListPagePopup(false);
        }}
        type="confirm"
        // msg="설계목록 화면으로 이동합니다. 청약접수를 다시 진행해주세요."
        // onOk={{
        //   handle: () => {
        //     history.push('/planList');
        //   },
        // }}
      >
        <div className="cont">
          <p className="layer-text text-center">
            설계목록 화면으로 이동합니다. <br />
            청약접수를 다시 진행해주세요.
          </p>
        </div>
        <div className="btn-group">
          <SatButton
            styleClass="btn-half cancel"
            label={'취소'}
            onClick={() => {
              setIsOpenMovePlanListPagePopup(false);
            }}
          />
          <SatButton
            styleClass="btn-half confirm"
            label={'확인'}
            onClick={
              handleReissueButtonClick(selectedReissueDocList)
              // history.push('/planList');
            }
          />
        </div>
      </SatModal>
    </>
  );
};

AdditionalDocPopup.propTypes = {
  hide: PropTypes.func.isRequired,
  postEachReissueReport: PropTypes.func.isRequired, // 발행버튼 클릭 핸들러
  handleReissueRequest: PropTypes.func.isRequired, // 재발행 선택 핸들러
};
AdditionalDocPopup.defaultProps = {};

export default memo(AdditionalDocPopup);
